/**
 * The four standing entry points for order processing, always visible at the
 * top of the dashboard: MSE greenlight, MSP store, Reorder, and Backbook
 * (modify & ship for existing pairs, new stands for pairs missing stands data).
 */

const DAY = new Date().getDay(); // 0=Sun ... 5=Fri

// One button treatment across all four flows — the cards, badges and chips
// carry the per-flow distinctions, the CTAs shouldn't.
const CTA =
  'w-full px-3 py-2 bg-indigo-600 text-white text-xs font-medium leading-tight rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5';

function ScheduleChip({ days, label }) {
  const isToday = days.includes(DAY);
  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
        isToday ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {label}
      {isToday && ' · today'}
    </span>
  );
}

export default function ProcessingHub({ mseEligible, mspCounts, onOpenGreenlight, onOpenMsp, onOpenReorder, onOpenBackbook, onOpenBackbookNewStands }) {
  const mspTotal = mspCounts?.total || 0;
  const mspHot = mspTotal > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
      <div className="px-5 py-3.5 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Order Processing</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-gray-100">
        {/* MSE — greenlight batches */}
        <div className="px-5 py-4 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-gray-900">MSE</p>
            <ScheduleChip days={[1, 3]} label="Mon & Wed" />
          </div>
          <p className="text-xs text-gray-400 mb-3 flex-1">
            Greenlight dealers from Capital One's sheet.
            {mseEligible > 0 && (
              <span className="block mt-1 text-gray-600">
                <span className="font-semibold">{mseEligible}</span> order{mseEligible !== 1 ? 's' : ''} in{' '}
                <span className="font-mono text-[10px]">codes_completed</span>
              </span>
            )}
          </p>
          <button
            onClick={onOpenGreenlight}
            className={CTA}
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Process MSE orders
          </button>
        </div>

        {/* MSP — paid store */}
        <div className={`px-5 py-4 flex flex-col ${mspHot ? 'bg-indigo-50/60' : ''}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              MSP
              {mspHot && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-indigo-600 px-1.5 py-0.5 rounded-full">
                  {mspTotal} pending
                </span>
              )}
            </p>
            <ScheduleChip days={[0, 1, 2, 3, 4, 5, 6]} label="As placed" />
          </div>
          <p className="text-xs text-gray-400 mb-3 flex-1">
            Store orders, batched into cards / stands.
            {mspHot && (
              <span className="block mt-1 text-indigo-700">
                {[
                  mspCounts.cards > 0 && `${mspCounts.cards} cards`,
                  mspCounts.stands > 0 && `${mspCounts.stands} stands`,
                  mspCounts.mixed > 0 && `${mspCounts.mixed} mixed`,
                ].filter(Boolean).join(' · ')}
                {mspCounts.withIssues > 0 && ` · ${mspCounts.withIssues} with issues`}
              </span>
            )}
          </p>
          <button
            onClick={onOpenMsp}
            className={CTA}
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z" />
            </svg>
            Process MSP orders
          </button>
        </div>

        {/* Reorder — Capital One sheet upload */}
        <div className="px-5 py-4 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-gray-900">Reorder</p>
            <ScheduleChip days={[5]} label="Fridays" />
          </div>
          <p className="text-xs text-gray-400 mb-3 flex-1">
            Upload Capital One's sheet, import to backend as kits / cards / stands batches.
          </p>
          <button
            onClick={onOpenReorder}
            className={CTA}
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Process reorders
          </button>
        </div>

        {/* Backbook — modify existing pairs, or create new stands (later) */}
        <div className="px-5 py-4 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-gray-900">Backbook</p>
            <ScheduleChip days={[5]} label="Fridays" />
          </div>
          <p className="text-xs text-gray-400 mb-3 flex-1">
            Ship-to addresses for existing card + stand pairs; batches cards vs stands.
          </p>
          <div className="flex flex-col gap-1.5">
            <button
              onClick={onOpenBackbook}
              className={CTA}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
              </svg>
              Process backbook — modify &amp; ship
            </button>
            <button
              onClick={onOpenBackbookNewStands}
              title="For pairs missing stands data — creates the stands orders via the import tool"
              className="w-full px-3 py-1.5 border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium leading-tight rounded-lg hover:bg-indigo-100 transition-colors"
            >
              Process backbook — new stands
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
